import {
  Component, ElementRef, OnInit
} from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { addDays, startOfDay, startOfMonth, format, addMonths, addWeeks, endOfMonth, isSameDay } from 'date-fns';
import { WsDay, WsDayType, WsEmployee, WsEmployeeLookup, WsEvent, WsProdCalendarDay, WsScheduleLookup } from './core/work-schedule.models';
import { buildMonthDays, buildWeekDays, createEventId, diffInMonths, getDateRange, getEventId, getEventIds, getMergedSequentialEvent, getPopupPosition, isDatesIntersection } from './core/work-schedule.utils';
import { NotificationService } from '../../../core/services/notification.service';
import { NotificationType } from '../../../core/services/notification-type';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { forkJoin } from 'rxjs';
import { WorkScheduleService } from '../../services/work-schedule.service';
import { Guid } from 'guid-typescript';
import { MatTooltip } from '@angular/material';
import { SecurityService } from '../../../core/services/security.service';

@Component({
  selector: 'work-schedule',
  templateUrl: './work-schedule.component.html',
  styleUrls: ['./work-schedule.component.scss',
    '../../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss'
  ]
})
export class WorkScheduleComponent implements OnInit {
  events: WsEvent[] = [];
  view: 'month' | 'week' = 'month';
  date: Date = new Date();
  currendDate: Date = new Date();

  prodCalendarDays: WsProdCalendarDay[] = [];
  employees: WsEmployee[] = [];
  employeesForLookup: WsEmployeeLookup[] = [];
  timeSchedulesForLookup: WsScheduleLookup[] = [];

  selectedTimeScheduleId: number | null = null;
  timeScheduleDateFrom: Date | null = null;

  isPopupVisible = false;
  selectedEvent: WsEvent | null = null;
  selectedEmployee: WsEmployee | null = null;
  selectedShiftEmployee: WsEmployee | null = null;
  popupStyle: any = {}; // динамическая позиция
  popupShiftStyle: any = {}; // динамическая позиция
  evNewStartDate: Date | null = null;
  evNewEndDate: Date | null = null;

  isShiftPopupVisible = false;

  currentPage = 1;
  pageSize = 50;
  totalItems = 0;
  totalPages = 0;

  _selectedEmployeeId: number | null = null;

  get selectedEmployeeId(): number {
    return this._selectedEmployeeId;
  }

  set selectedEmployeeId(value: number) {
    this._selectedEmployeeId = value;
    this.loadEmployees(this._selectedEmployeeId);
  }

  days: WsDay[] = [];
  dayWidth = 38;
  currentYear = this.date.getFullYear();

  /** список ID дроп-зон */
  dropLists: string[] = [];

  public dropDownFilterSettings: DropDownFilterSettings = { caseSensitive: false, operator: 'contains' };
  public dropDownVirtualSettings: any = {
    itemHeight: 28
  };

  palette = [
    { type: WsDayType.Sick, icon: '💊', label: 'Больничный' },
    { type: WsDayType.Vacation, icon: '🌴', label: 'Отпуск' },
    { type: WsDayType.DayOff, icon: '🚶‍♂️', label: 'Отгул' },
    { type: WsDayType.Extra, icon: '👨‍💻', label: 'Доп. рабочий день' },
    { type: WsDayType.BusinessTrip, icon: '💼', label: 'Командировка' },
  ];

  constructor(private host: ElementRef,
    protected notificationService: NotificationService,
    public workScheduleService: WorkScheduleService,
    private securityService: SecurityService
  ) {
  }

  get isFullReadonly(): boolean {
    return this.securityService.isSpecialRightClaimValid('FullReadonly');
  }

  public get gridHeight(): number {
		return window.innerHeight - 380;
	}

  ngOnInit() {
    this.loadData();
  }

  loadData(): void {
    const getProdCalendar$ = this.workScheduleService.getProdCalendar(this.currentYear);
    const getEmployeesLookup$ = this.workScheduleService.getEmployeesLookup();
    const getEmployees$ = this.workScheduleService.getEmployees(this.currentPage, this.pageSize, this.date, this._selectedEmployeeId);
    const getTimeSchedulesLookup$ = this.workScheduleService.getTimeSchedulesLookup();

    forkJoin([getProdCalendar$, getEmployeesLookup$, getEmployees$, getTimeSchedulesLookup$])
      .subscribe(([prodCalendars, employeesForLookup, employees, timeSchedulesForLookup]) => {
        this.timeSchedulesForLookup = timeSchedulesForLookup;
        this.prodCalendarDays = prodCalendars;
        this.employees = employees.data;

        this.events = [].concat(...this.employees.map(e => e.events));

        this.totalItems = employees.totalCount;
        this.totalPages = Math.ceil(this.totalItems / this.pageSize);

        if (this.currentPage > this.totalPages) {
          this.currentPage = this.totalPages
        }

        this.employeesForLookup = employeesForLookup;

        this.rebuildDays();

        const root = this.host.nativeElement.querySelector('.ws-root');
        if (root) root.style.setProperty('--dayw', this.dayWidth + 'px');

        this.events.forEach(ev => {
          ev.start = startOfDay(ev.start);
          ev.end = startOfDay(ev.end);
        });
      })
  }

  loadProdCalendar() {
    this.workScheduleService.getProdCalendar(this.currentYear).subscribe(res => {
      this.prodCalendarDays = res;
    });
  }

  loadEmployees(filteredEmployeeId: number = null): void {
    this.workScheduleService.getEmployees(this.currentPage, this.pageSize, this.date, filteredEmployeeId)
      .subscribe(res => {
        this.employees = res.data;
        this.events = [].concat(...this.employees.map(e => e.events));
        this.totalItems = res.totalCount;
        this.totalPages = Math.ceil(this.totalItems / this.pageSize);

        if (this.currentPage > this.totalPages) {
          this.currentPage = this.totalPages
        }

        this.rebuildDays();

        this.events.forEach(ev => {
          ev.start = startOfDay(ev.start);
          ev.end = startOfDay(ev.end);
        });
      });
  }

  getIsoDate(d: Date): string {
    const formatted = d.toISOString().slice(0, 10).replace(/-/g, '');
    return formatted;
  }

  /** создаёт ленту дней */
  rebuildDays() {
    if (this.view === 'month') {
      this.date = startOfMonth(this.date);
      this.days = buildMonthDays(this.date, this.prodCalendarDays);
    } else {
      this.days = buildWeekDays(this.date, this.prodCalendarDays);
    }

    this.dropLists = [];

    let empIds = this.employees.map(e => e.id);

    empIds.forEach(e => {
      this.days.forEach(d => {
        this.dropLists.push(`${e}_${this.getIsoDate(d.date)}`);
      });
    });
  }

  getLabelByType(type: WsDayType): string {
    return this.palette.find(p => p.type === type).label;
  }

  /** управление месяцами/неделями */
  prev() {
    switch (this.view) {
      case 'month':
        this.date = addMonths(this.date, -1);
        break;
      case 'week':
        this.date = addWeeks(this.date, -1);
        break;
    }

    if (this.currentYear !== this.date.getFullYear()) {
      this.currentYear = this.date.getFullYear();
      this.loadProdCalendar();
    }

    this.closePopup();
    this.closeShiftPopup();
    this.loadEmployees(this._selectedEmployeeId);
  }
  next() {
    switch (this.view) {
      case 'month':
        this.date = addMonths(this.date, 1);
        break;
      case 'week':
        this.date = addWeeks(this.date, 1);
        break;
    }

    if (this.currentYear !== this.date.getFullYear()) {
      this.currentYear = this.date.getFullYear();
      this.loadProdCalendar();
    }

    this.closePopup();
    this.closeShiftPopup();
    this.loadEmployees(this._selectedEmployeeId);
  }

  switch(view: 'month' | 'week') {
    this.view = view;
    this.rebuildDays();
  }

  /** формат */
  dayLabel(d: Date) { return format(d, 'D'); }
  dayName(d: Date) {
    const ru = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
    return ru[d.getDay()];
  }

  isCurrentDay(d: WsDay): boolean {
    return isSameDay(d.date, this.currendDate);
  }

  balloonMsg = '';

  eventMouseMove(event: MouseEvent, ev: WsEvent) {
    this.balloonMsg = `${event.clientX} : ${event.clientY}`;

    if (!this.days.length) return {};

    const startView = this.days[0];
    const endView = this.days[this.days.length - 1];

    // Обрезаем событие по границам видимого диапазона
    const start = ev.start < startView.date ? startView.date : ev.start;
    const end = ev.end > endView.date ? endView.date : ev.end;

    // Количество дней (включительно)
    const dayCount =
      Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    // Смещение от начала диапазона
    const offsetDays =
      Math.round((start.getTime() - startView.date.getTime()) / (1000 * 60 * 60 * 24));

    return {
      left: `${offsetDays * this.dayWidth}px`,
      width: `${dayCount * this.dayWidth}px`,
    };
  }

  getRequestsCount(emp: WsEmployee, d: WsDay): string {
    let day = emp.daysInfo.find(f => isSameDay(new Date(f.date), d.date));
    if (day) {
      return day.requestsCount.toString();
    }

    return '';
  }

  /** рабочие дни */
  isWorkDay(emp: WsEmployee, d: WsDay): boolean {
    // if (!emp.workDays || emp.workDays.length === 0) return false;
    // const wd = d.getDay() === 0 ? 7 : d.getDay();
    // return emp.workDays.indexOf(wd) !== -1;

    let type = d.type;

    let empDay = emp.days.find(fd => isSameDay(new Date(fd.date), d.date));

    if (empDay) {
      type = empDay.type;
    }

    if ([WsDayType.Workday, WsDayType.PreHoliday].includes(type)) {
      return true;
    }

    if ([WsDayType.Holiday, WsDayType.Weekend, WsDayType.Empty].includes(type)) {
      return false;
    }

    return true;
  }

  isHoliday(d: WsDay): boolean {
    if (d.type === WsDayType.Holiday) {
      return true;
    }

    return false;
  }

  isHolidayByEmp(emp: WsEmployee, d: WsDay): boolean {
    let type = d.type;

    let empDay = emp.days.find(fd => isSameDay(new Date(fd.date), d.date));

    if (empDay) {
      type = empDay.type;
    }

    if ([WsDayType.Holiday].includes(type)) {
      return true;
    }

    return false;
  }

  isWeekend(d: WsDay): boolean {
    return this.isHoliday(d) || d.type === WsDayType.Weekend;
  }

  /** события сотрудника */
  eventsFor(empId: Guid): WsEvent[] {
    if (!this.days.length) return [];

    const startView = this.days[0];
    const endView = this.days[this.days.length - 1];

    // Показываем все события, которые пересекаются с диапазоном видимых дней
    let targetEvents = this.events.filter(ev =>
      ev.employeeId === empId &&
      ev.start.getTime() <= endView.date.getTime() && // началось до конца диапазона
      ev.end.getTime() >= startView.date.getTime()    // и закончилось после начала
    );

    targetEvents.forEach(ev => {
      ev.icon = this.palette.find(p => p.type === ev.type).icon
    });

    return targetEvents;
  }

  /** классы событий */
  eventClass(t: WsDayType) {
    return 'evt-' + WsDayType[t].toLowerCase();
  }

  /** позиция события */
  eventPosition(ev: WsEvent, days: WsDay[]) {
    if (!days.length) return {};

    const startView = days[0];
    const endView = days[days.length - 1];

    // Обрезаем событие по границам видимого диапазона
    const start = ev.start < startView.date ? startView.date : ev.start;
    const end = ev.end > endView.date ? endView.date : ev.end;

    // Количество дней (включительно)
    const dayCount =
      Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    // Смещение от начала диапазона
    const offsetDays =
      Math.round((start.getTime() - startView.date.getTime()) / (1000 * 60 * 60 * 24));

    return {
      left: `${offsetDays * this.dayWidth}px`,
      width: `${dayCount * this.dayWidth}px`,
    };
  }

  /** drop из панели действий */
  onCellDrop(employeeId: Guid, date: Date, event: CdkDragDrop<any>) {
    if (this.isFullReadonly) {
      return;
    }

    const data = event.item.data;
    if (!data || !data.fromPalette) return;

    const start = startOfDay(date);
    const end = startOfDay(date);

    // 🚫 Проверка пересечений
    if (this.hasOverlap(employeeId, start, end)) {
      // например, визуально подсветим ячейку или покажем сообщение
      this.notificationService.error({
        title: 'Ошибка календаря',
        message: 'Пересечение с существующим событием',
        notificationType: NotificationType.Toast
      });
      event.item.reset();
      return;
    }

    const newEvent: WsEvent = {
      id: 'ev-' + this.nextId(),
      employeeId,
      type: data.type,
      start: startOfDay(date),
      end: startOfDay(date),
      icon: data.icon
    };

    // if (this.hasOverlap(employeeId, newEvent)) {
    //   console.warn('Пересечение событий — отменено');
    //   return;
    // }

    this.workScheduleService.createEmployeeEvent({
      dateFrom: newEvent.start,
      dateTo: newEvent.end,
      employeeIntegraCode: newEvent.employeeId,
      rangeDaysType: newEvent.type
    }).subscribe(res => {
      if (res.isSuccessful) {
        newEvent.id = createEventId(res.data),
          this.events.push(newEvent);
      }
    });

    // Возвращаем элемент обратно на панель
    // event.previousContainer.element.nativeElement.appendChild(event.item.element.nativeElement);
  }

  private nextId(): string {
    // Если randomUUID доступен
    if ('randomUUID' in crypto) {
      return (crypto as any).randomUUID();
    }

    // Fallback на старый способ (RFC4122 v4)
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /** просто "вспомогательный" drop на саму панель — ничего не делаем */
  onPaletteDrop(_event: CdkDragDrop<any>) { }

  disableReorderFor = (ids: string[]) => (drag, drop) =>
    ids.indexOf(drop.id) === -1;

  hasOverlap(empId: Guid, start: Date, end: Date, excludeId?: string[]): boolean {

    let dateRangeSource = getDateRange(start, end);

    let targetEvents = this.events.filter(e => e.employeeId == empId && (!excludeId || !excludeId.includes(e.id)));
    let dateRangeEvents = [];

    targetEvents.forEach(f => {
      dateRangeEvents.push(...getDateRange(f.start, f.end));
    });

    return isDatesIntersection(dateRangeSource, dateRangeEvents);
  }

  /** перемещение события внутри календаря */
  onEventMoved(emp: WsEmployee, ev: WsEvent, e: any) {
    const dx = e.distance && e.distance.x ? e.distance.x : 0;
    const shift = Math.round(dx / this.dayWidth);
    if (!shift) return;

    let oldStart = ev.start;
    let oldEnd = ev.end;

    ev.start = addDays(ev.start, shift);
    ev.end = addDays(ev.end, shift);

    this.workScheduleService.updateEmployeeEvent({
      employeeCustomTimeScheduleId: getEventId(ev.id),
      dateFrom: ev.start,
      dateTo: ev.end,
      employeeIntegraCode: ev.employeeId,
      rangeDaysType: ev.type
    }).subscribe(res => {
      if (!res.isSuccessful) {
        ev.start = oldStart;
        ev.end = oldEnd;
      }
    }, _ => {
      ev.start = oldStart;
      ev.end = oldEnd;
    });
  }

  /** ресайз (через директиву) */
  onResizeEnd(ev: WsEvent, payload: { side: 'left' | 'right'; deltaDays: number; nativeElement: HTMLElement, startWidth: number, startLeft: number }) {
    if (this.isFullReadonly) {
      return;
    }

    function resetTransition(): void {
      payload.nativeElement.style.width = payload.startWidth + 'px';
      payload.nativeElement.style.left = payload.startLeft + 'px';
      payload.nativeElement.style.transform = '';
    }

    const d = payload.deltaDays || 0;
    if (!d) return;

    if (payload.side === 'left') {
      let newStart = addDays(ev.start, d);
      if (newStart > ev.end) newStart = ev.end;

      if (this.hasOverlap(ev.employeeId, newStart, ev.end, [ev.id])) {
        this.notificationService.error({
          title: 'Ошибка календаря',
          message: 'Пересечение с существующим событием',
          notificationType: NotificationType.Toast
        });
        // сбрасываем визуальное смещение, возвращая контроль ngStyle
        resetTransition();
        return;
      }

      this.workScheduleService.updateEmployeeEvent({
        employeeCustomTimeScheduleId: getEventId(ev.id),
        dateFrom: newStart,
        dateTo: ev.end,
        employeeIntegraCode: ev.employeeId,
        rangeDaysType: ev.type
      }).subscribe(res => {
        if (res.isSuccessful) {
          ev.start = newStart;
        } else {
          resetTransition();
        }
      }, _ => {
        resetTransition();
      });
    } else {
      let newEnd = addDays(ev.end, d);
      if (newEnd < ev.start) newEnd = ev.start;

      if (this.hasOverlap(ev.employeeId, ev.start, newEnd, [ev.id])) {
        this.notificationService.error({
          title: 'Ошибка календаря',
          message: 'Пересечение с существующим событием',
          notificationType: NotificationType.Toast
        });
        // сбрасываем визуальное смещение, возвращая контроль ngStyle
        payload.nativeElement.style.width = payload.startWidth + 'px';
        payload.nativeElement.style.left = payload.startLeft + 'px';
        payload.nativeElement.style.transform = '';
        return;
      }

      this.workScheduleService.updateEmployeeEvent({
        employeeCustomTimeScheduleId: getEventId(ev.id),
        dateFrom: ev.start,
        dateTo: newEnd,
        employeeIntegraCode: ev.employeeId,
        rangeDaysType: ev.type
      }).subscribe(res => {
        if (res.isSuccessful) {
          ev.end = newEnd;
        } else {
          resetTransition();
        }
      }, _ => {
        resetTransition();
      });
    }

    // необязательно, но можно ограничить по диапазону текущей ленты days:
    const minDate = this.days[0];
    const maxDate = this.days[this.days.length - 1];
    if (ev.start < minDate.date) {
      this.workScheduleService.updateEmployeeEvent({
        employeeCustomTimeScheduleId: getEventId(ev.id),
        dateFrom: minDate.date,
        dateTo: ev.end,
        employeeIntegraCode: ev.employeeId,
        rangeDaysType: ev.type
      }).subscribe(res => {
        if (res.isSuccessful) {
          ev.start = minDate.date;
        } else {
          resetTransition();
        }
      }, _ => {
        resetTransition();
      });
    }

    if (ev.end > maxDate.date) {
      this.workScheduleService.updateEmployeeEvent({
        employeeCustomTimeScheduleId: getEventId(ev.id),
        dateFrom: ev.start,
        dateTo: maxDate.date,
        employeeIntegraCode: ev.employeeId,
        rangeDaysType: ev.type
      }).subscribe(res => {
        if (res.isSuccessful) {
          ev.end = maxDate.date;
        } else {
          resetTransition();
        }
      }, _ => {
        resetTransition();
      });
    }

    // сбрасываем визуальное смещение, возвращая контроль ngStyle
    payload.nativeElement.style.transform = '';
  }



  private ghostEl: HTMLElement | null = null;

  onPaletteDragStart(event: any, p: any) {
    const icon = document.createElement('div');
    icon.classList.add('drag-ghost-icon', 'evt-' + p.type);
    icon.innerHTML = p.icon;

    document.body.appendChild(icon);
    this.ghostEl = icon;

    const move = (e: MouseEvent) => {
      if (this.ghostEl) {
        this.ghostEl.style.left = e.pageX + 10 + 'px';
        this.ghostEl.style.top = e.pageY + 10 + 'px';
      }
    };
    document.addEventListener('mousemove', move);
    event.source['_ghostMove'] = move; // сохранить, чтобы удалить потом
  }

  onPaletteDragEnd(event: any) {
    if (this.ghostEl) {
      this.ghostEl.remove();
      this.ghostEl = null;
    }
    if (event.source['_ghostMove']) {
      document.removeEventListener('mousemove', event.source['_ghostMove']);
    }
  }

  onManualDragStart(event: any, p: any) {
    if (this.isFullReadonly) {
      return;
    }

    // Отключаем стандартное поведение CDK
    // event.source._dragRef._activeTransform = { x: 0, y: 0 }; // обнуляем смещение
    // event.source.element.nativeElement.style.transform = 'none';
    event.source.element.nativeElement.style.visibility = 'hidden';
    document.body.style.cursor = 'grabbing'
    // event.source.element.nativeElement.innerText = '';
    // создаём свою логику
    // const icon = document.createElement('div');
    // icon.className = 'drag-ghost-icon';
    // icon.innerHTML = '💊';
    // document.body.appendChild(icon);
    // this.ghostEl = icon;

    // const move = (e: MouseEvent) => {
    //   if (this.ghostEl) {
    //     this.ghostEl.style.left = e.pageX + 8 + 'px';
    //     this.ghostEl.style.top = e.pageY + 8 + 'px';
    //   }
    // };
    // document.addEventListener('mousemove', move);
    // event.source['_ghostMove'] = move;
  }

  onManualDragEnd(event: any) {
    // Удаляем кастомный призрак
    // if (this.ghostEl) {
    //   this.ghostEl.remove();
    //   this.ghostEl = null;
    // }
    // if (event.source['_ghostMove']) {
    //   document.removeEventListener('mousemove', event.source['_ghostMove']);
    // }

    // // Сбрасываем стандартное смещение CDK
    // event.source._dragRef.reset();

    event.source.element.nativeElement.style.visibility = 'visible';
    document.body.style.cursor = '';
  }

  // внутри WorkScheduleComponent
  private dragOriginLeft = 0;   // начальная позиция left (px)
  private lastX = 0;

  onEventDragStart(event: any, ev: WsEvent) {
    if (this.isFullReadonly) {
      return;
    }

    // исходная позиция left из [ngStyle]
    const styleLeft = this.eventPosition(ev, this.days).left;
    this.dragOriginLeft = parseFloat(styleLeft);
  }

  onEventDragMove(event: any) {
    if (this.isFullReadonly) {
      return;
    }

    if (!this.days.length) return;

    // координаты контейнера col-days
    const htmlElement = event.source.element.nativeElement as HTMLElement;
    const colDaysEl = (event.source.element.nativeElement.closest('.col-days')) as HTMLElement;
    const rect = colDaysEl.getBoundingClientRect();
    const calendarLeft = rect.left + window.scrollX;

    // текущая позиция курсора
    const pointerX = event.pointerPosition.x + window.scrollX;

    // вычисляем смещение от начала календаря
    const relX = pointerX - calendarLeft;
    const maxRelX = rect.width - parseFloat(event.source.element.nativeElement.style.width);

    // квантование по дням
    const clampedX = Math.max(0, Math.min(relX, maxRelX));
    const step = Math.round(clampedX / this.dayWidth);
    const snappedX = step * this.dayWidth;

    // визуально двигаем событие, но не выходим за края
    const el = event.source.element.nativeElement as HTMLElement;
    el.style.transform = `translate3d(${snappedX - this.dragOriginLeft}px, 0, 0)`;

    this.lastX = snappedX;
  }

  onEventDragEnd(event: any, ev: WsEvent) {
    if (this.isFullReadonly) {
      return;
    }

    const dx = this.lastX - this.dragOriginLeft;
    const shift = Math.round(dx / this.dayWidth);

    const newStart = addDays(ev.start, shift);
    const newEnd = addDays(ev.end, shift);
    const el = event.source.element.nativeElement as HTMLElement;

    // 🚫 Проверка пересечения
    if (this.hasOverlap(ev.employeeId, newStart, newEnd, [ev.id])) {
      this.notificationService.error({
        title: 'Ошибка календаря',
        message: 'Пересечение с существующим событием',
        notificationType: NotificationType.Toast
      });
      el.style.transform = '';
      return;
    }

    if (shift !== 0) {
      let oldStart = ev.start;
      let oldEnd = ev.end;

      ev.start = addDays(ev.start, shift);
      ev.end = addDays(ev.end, shift);

      this.workScheduleService.updateEmployeeEvent({
        employeeCustomTimeScheduleId: getEventId(ev.id),
        dateFrom: ev.start,
        dateTo: ev.end,
        employeeIntegraCode: ev.employeeId,
        rangeDaysType: ev.type
      }).subscribe(res => {
        if (!res.isSuccessful) {
          ev.start = oldStart;
          ev.end = oldEnd;
        }
      }, _ => {
        ev.start = oldStart;
        ev.end = oldEnd;
      });
    }

    // сбрасываем визуальное смещение, возвращая контроль ngStyle
    el.style.transform = '';
  }

  openEventPopup(mouseEvent: MouseEvent, ev: WsEvent, emp: WsEmployee) {
    mouseEvent.stopPropagation();
    let mergedEvent = getMergedSequentialEvent(
      this.events.filter(f => f.employeeId === ev.employeeId && f.type === ev.type),
      ev.id
    );
    this.evNewStartDate = mergedEvent.start;
    this.evNewEndDate = mergedEvent.end;
    this.selectedEvent = ev;
    this.selectedEmployee = emp;
    this.isPopupVisible = true;

    let position = getPopupPosition(mouseEvent.target, 360, 180, 70);

    // окончательная позиция
    this.popupStyle = {
      position: 'fixed',
      left: `${position.left}px`,
      top: `${position.top}px`
    };
  }

  openShiftPopup(mouseEvent: MouseEvent, emp: WsEmployee) {
    this.isShiftPopupVisible = true;
    let position = getPopupPosition(mouseEvent.target, 390, 180, 10);
    this.selectedShiftEmployee = emp;
    // окончательная позиция
    this.popupShiftStyle = {
      position: 'fixed',
      left: `${position.left}px`,
      top: `${position.top}px`
    };
  }

  closeShiftPopup() {
    this.selectedTimeScheduleId = null;
    this.timeScheduleDateFrom = null;
    this.selectedShiftEmployee = null;
    this.isShiftPopupVisible = false;
  }

  setEventPeriod(ev: WsEvent | null) {
    if (this.isFullReadonly) {
      return;
    }

    if (!ev) return;

    let mergedEvent = getMergedSequentialEvent(
      this.events.filter(f => f.employeeId === ev.employeeId && f.type === ev.type),
      ev.id
    );

    if (mergedEvent.start === this.evNewStartDate && mergedEvent.end === this.evNewEndDate) {
      this.closePopup();
      return;
    }

    // 🚫 Проверка пересечения
    if (this.hasOverlap(ev.employeeId, this.evNewStartDate, this.evNewEndDate, mergedEvent.ids)) {
      this.notificationService.error({
        title: 'Ошибка календаря',
        message: 'Пересечение с существующим событием',
        notificationType: NotificationType.Toast
      });
      return;
    }

    let periodStart = `${this.evNewStartDate.getMonth()}.${this.evNewStartDate.getFullYear()}`;
    let periodEnd = `${this.evNewEndDate.getMonth()}.${this.evNewEndDate.getFullYear()}`;
    let stepDate = startOfMonth(new Date(this.evNewStartDate));

    if (periodStart !== periodEnd) {
      this.workScheduleService.deleteEmployeeEvents({
        ids: getEventIds(mergedEvent.ids)
      }).subscribe(dRes => {
        if (dRes.isSuccessful) {
          this.events = this.events.filter(e => !mergedEvent.ids.includes(e.id));

          const newEventStart: WsEvent = {
            id: 'ev-' + this.nextId(),
            employeeId: ev.employeeId,
            type: ev.type,
            start: startOfDay(this.evNewStartDate),
            end: startOfDay(endOfMonth(this.evNewStartDate)),
            icon: ev.icon
          };

          this.workScheduleService.createEmployeeEvent({
            dateFrom: newEventStart.start,
            dateTo: newEventStart.end,
            employeeIntegraCode: ev.employeeId,
            rangeDaysType: ev.type
          }).subscribe(cRes => {
            if (cRes.isSuccessful) {
              newEventStart.id = createEventId(cRes.data);
              this.events.push(newEventStart);
            }
          });

          const newEventEnd: WsEvent = {
            id: 'ev-' + this.nextId(),
            employeeId: ev.employeeId,
            type: ev.type,
            start: startOfDay(startOfMonth(this.evNewEndDate)),
            end: startOfDay(this.evNewEndDate),
            icon: ev.icon
          };

          this.workScheduleService.createEmployeeEvent({
            dateFrom: newEventEnd.start,
            dateTo: newEventEnd.end,
            employeeIntegraCode: ev.employeeId,
            rangeDaysType: ev.type
          }).subscribe(cRes => {
            if (cRes.isSuccessful) {
              newEventEnd.id = createEventId(cRes.data);
              this.events.push(newEventEnd);
            }
          });

          let monthCount = diffInMonths(this.evNewStartDate, this.evNewEndDate) - 1;

          for (let index = 0; index < monthCount; index++) {
            stepDate.setMonth(stepDate.getMonth() + 1);

            const newEvent: WsEvent = {
              id: 'ev-' + this.nextId(),
              employeeId: ev.employeeId,
              type: ev.type,
              start: startOfDay(startOfMonth(stepDate)),
              end: startOfDay(endOfMonth(stepDate)),
              icon: ev.icon
            };

            this.workScheduleService.createEmployeeEvent({
              dateFrom: newEvent.start,
              dateTo: newEvent.end,
              employeeIntegraCode: ev.employeeId,
              rangeDaysType: ev.type
            }).subscribe(cRes => {
              if (cRes.isSuccessful) {
                newEvent.id = createEventId(cRes.data);
                this.events.push(newEvent);
              }
            });
          }
        }
      });
    } else {
      if (mergedEvent.ids.length > 1) {
        this.workScheduleService.deleteEmployeeEvents({
          ids: getEventIds(mergedEvent.ids)
        }).subscribe(res => {
          if (res.isSuccessful) {
            this.events = this.events.filter(e => !mergedEvent.ids.includes(e.id));

            const newEvent: WsEvent = {
              id: 'ev-' + this.nextId(),
              employeeId: ev.employeeId,
              type: ev.type,
              start: startOfDay(this.evNewStartDate),
              end: startOfDay(this.evNewEndDate),
              icon: ev.icon
            };

            this.workScheduleService.createEmployeeEvent({
              dateFrom: newEvent.start,
              dateTo: newEvent.end,
              employeeIntegraCode: ev.employeeId,
              rangeDaysType: ev.type
            }).subscribe(cRes => {
              if (cRes.isSuccessful) {
                newEvent.id = createEventId(cRes.data);
                this.events.push(newEvent);
              }
            });
          }
        });
      } else {
        this.workScheduleService.updateEmployeeEvent({
          employeeCustomTimeScheduleId: getEventId(ev.id),
          dateFrom: this.evNewStartDate,
          dateTo: this.evNewEndDate,
          employeeIntegraCode: ev.employeeId,
          rangeDaysType: ev.type
        }).subscribe(uRes => {
          if (uRes.isSuccessful) {
            ev.start = this.evNewStartDate;
            ev.end = this.evNewEndDate;
          }
        });
      }
    }

    this.closePopup();
  }

  closePopup() {
    this.isPopupVisible = false;
    this.selectedEvent = null;
    this.selectedEmployee = null;
  }

  deleteEvent(ev: WsEvent | null) {
    if (this.isFullReadonly) {
      return;
    }

    if (!ev) return;
    if (confirm('Удалить событие?')) {
      let mergedEvent = getMergedSequentialEvent(
        this.events.filter(f => f.employeeId === ev.employeeId && f.type === ev.type),
        ev.id
      );

      this.workScheduleService.deleteEmployeeEvents({
        ids: getEventIds(mergedEvent.ids)
      }).subscribe(res => {
        if (res.isSuccessful) {
          this.events = this.events.filter(e => !mergedEvent.ids.includes(e.id));
          this.closePopup();
        }
      });
    }
  }

  addShift() {
    if (this.isFullReadonly) {
      return;
    }

    if (this.selectedTimeScheduleId === null || this.timeScheduleDateFrom === null) {
      this.notificationService.error({
        title: 'Ошибка календаря',
        message: 'Не заполнена смена или дата',
        notificationType: NotificationType.Toast
      });
      return;
    }

    this.workScheduleService.createEmployeeTimeShedule({
      employeeIntegraCode: this.selectedShiftEmployee.id,
      startsFrom: this.timeScheduleDateFrom,
      timeScheduleId: this.selectedTimeScheduleId
    }).subscribe(res => {
      if (res.isSuccessful) {
        this.loadData();
        this.closeShiftPopup();
      }
    });
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.closePopup();
      this.closeShiftPopup();
      this.currentPage++;
      this.loadEmployees(this._selectedEmployeeId);
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.closePopup();
      this.closeShiftPopup();
      this.currentPage--;
      this.loadEmployees(this._selectedEmployeeId);
    }
  }

  // закрытие по клику вне
  // @HostListener('document:click')
  // onDocumentClick() {
  //   if (this.isPopupVisible) this.closePopup();
  // }
}

